import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InboxIcon from "@mui/icons-material/Inbox";
import MenuBookIcon from "@mui/icons-material/MenuBook";

import {
  useGetPublicMockTestsQuery,
  useGetPublicMockTestBooksQuery,
} from "@/store/api/user/publicMockTestApi";

import MockTestCard from "./MockTestCard";
import ModeSelectDialog from "./ModeSelectDialog";
import {
  BRAND_GRADIENT,
  BRAND_PRIMARY,
  BRAND_TINT_BG,
  BRAND_TINT_BORDER,
  UNCATEGORISED,
} from "./theme";

const PublicMockTestBookDetail = () => {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const isOther = bookId === UNCATEGORISED;

  // For a real book we ask the server to filter; for "Other" we pull the
  // full list and keep only exams with no `book`. The full-list query is
  // cached by RTK Query, so navigating from the index page is instant.
  const { data, isLoading, isError, error } = useGetPublicMockTestsQuery(
    isOther ? {} : { book: bookId },
    { refetchOnMountOrArgChange: true }
  );
  const { data: books = [] } = useGetPublicMockTestBooksQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const [selectedExam, setSelectedExam] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [bookId]);

  const items = useMemo(() => data?.items || [], [data]);

  const exams = useMemo(() => {
    if (isOther) return items.filter((e) => !e.book || !e.book._id);
    // Defensive: even with the `book` server filter, double-check on the
    // client — older APIs may ignore unknown params.
    return items.filter((e) => e.book?._id === bookId);
  }, [items, isOther, bookId]);

  const book = useMemo(() => {
    if (isOther) return null;
    return (
      books.find((b) => b._id === bookId) ||
      exams[0]?.book ||
      { _id: bookId, title: "Collection" }
    );
  }, [isOther, books, bookId, exams]);

  const handleStart = ({ mode, parts }) => {
    const params = new URLSearchParams({ mode });
    if (mode === "part" && parts.length > 0) {
      params.set("parts", parts.join(","));
    }
    navigate(`/mock-tests/${selectedExam._id}/run?${params.toString()}`);
  };

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Container maxWidth="xl">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/mock-tests")}
          sx={{
            mb: 2,
            color: BRAND_PRIMARY,
            fontWeight: 600,
            textTransform: "none",
            "&:hover": { background: BRAND_TINT_BG },
          }}
        >
          All collections
        </Button>

        {/* Book hero */}
        <Box
          sx={{
            mb: 4,
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            background: isOther
              ? "linear-gradient(135deg, #94a3b8 0%, #cbd5e1 100%)"
              : BRAND_GRADIENT,
            color: "white",
            boxShadow: isOther
              ? "0 12px 28px rgba(100,116,139,0.18)"
              : "0 20px 40px rgba(124,58,237,0.22)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: "rgba(255,255,255,0.22)",
                display: "inline-flex",
              }}
            >
              {isOther ? (
                <InboxIcon sx={{ fontSize: 32 }} />
              ) : (
                <MenuBookIcon sx={{ fontSize: 32 }} />
              )}
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography
                variant="h3"
                component="h1"
                sx={{ fontWeight: 800, lineHeight: 1.1 }}
              >
                {isOther ? "Other tests" : book?.title || "Collection"}
              </Typography>
              {!isOther && book?.description && (
                <Typography
                  variant="body1"
                  sx={{ opacity: 0.92, mt: 0.75, maxWidth: 720 }}
                >
                  {book.description}
                </Typography>
              )}
              {isOther && (
                <Typography variant="body1" sx={{ opacity: 0.92, mt: 0.75 }}>
                  Standalone tests not part of any collection.
                </Typography>
              )}
            </Box>
            {!isOther && book?.year != null && (
              <Chip
                label={book.year}
                sx={{
                  bgcolor: "rgba(255,255,255,0.28)",
                  color: "white",
                  fontWeight: 700,
                  border: "1px solid rgba(255,255,255,0.35)",
                }}
              />
            )}
            <Chip
              label={`${exams.length} test${exams.length === 1 ? "" : "s"}`}
              sx={{
                bgcolor: "rgba(255,255,255,0.28)",
                color: "white",
                fontWeight: 700,
                border: "1px solid rgba(255,255,255,0.35)",
              }}
            />
          </Box>
        </Box>

        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: BRAND_PRIMARY }} />
          </Box>
        )}

        {isError && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error?.data?.message || "Failed to load tests for this collection."}
          </Alert>
        )}

        {!isLoading && !isError && exams.length === 0 && (
          <Alert
            severity="info"
            sx={{
              borderRadius: 2,
              bgcolor: BRAND_TINT_BG,
              border: `1px solid ${BRAND_TINT_BORDER}`,
              "& .MuiAlert-icon": { color: BRAND_PRIMARY },
            }}
          >
            No tests in this collection yet.
          </Alert>
        )}

        {!isLoading && !isError && exams.length > 0 && (
          <Grid container spacing={3}>
            {exams.map((exam) => (
              <Grid item xs={12} sm={6} md={4} key={exam._id}>
                <MockTestCard exam={exam} onStart={setSelectedExam} />
              </Grid>
            ))}
          </Grid>
        )}

        <ModeSelectDialog
          open={!!selectedExam}
          exam={selectedExam}
          onClose={() => setSelectedExam(null)}
          onStart={handleStart}
        />
      </Container>
    </Box>
  );
};

export default PublicMockTestBookDetail;
